// src/lib/prefetchSidebarLists.js
import api from "../axiosConfig";
import { getStoredToken, getStoredUser } from "./authStorage";

/* -------------------- shared helpers (copy-safe) -------------------- */
function decodePayload(tok) {
  try {
    const b64 = tok.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

function resolveCompanyId() {
  const tok =
    getStoredToken() ||
    window.localStorage.getItem("authToken") ||
    window.sessionStorage.getItem("authToken") ||
    "";

  const p = tok ? decodePayload(tok) : null;
  const fromJwt = p?.company_id ?? p?.companyId;

  const n = Number(fromJwt);
  if (Number.isFinite(n) && n > 0) return n;

  const direct =
    window.sessionStorage.getItem("companyId") ||
    window.sessionStorage.getItem("company_id") ||
    window.localStorage.getItem("companyId") ||
    window.localStorage.getItem("company_id") ||
    null;

  if (Number.isFinite(Number(direct)) && Number(direct) > 0) {
    return Number(direct);
  }

  const storedUser = getStoredUser();
  const fromUser =
    storedUser?.company_id ??
    storedUser?.companyId ??
    null;

  if (Number.isFinite(Number(fromUser)) && Number(fromUser) > 0) {
    return Number(fromUser);
  }

  return null;
}

function safeRead(key) {
  try { return JSON.parse(window.localStorage.getItem(key) || "null"); }
  catch { return null; }
}

function safeWrite(key, payload) {
  try { window.localStorage.setItem(key, JSON.stringify(payload)); }
  catch {}
}

// normaliza respostas tipo [] ou { items: [] }
function extractItems(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

async function tryFirstOk(urls) {
  for (const url of urls) {
    if (!url) continue;
    try {
      const res = await api.get(url);
      return { url, data: res?.data };
    } catch {
      // tenta o próximo
    }
  }
  return { url: null, data: null };
}

async function prefetchList({
  cacheKey,
  urls,
  force = false,
  maxAgeMs = 60_000,
}) {
  const cache = safeRead(cacheKey);
  const now = Date.now();
  const hasFresh =
    !!cache?.items &&
    Array.isArray(cache.items) &&
    (now - (cache.ts || 0) < maxAgeMs);

  if (!force && hasFresh) return;

  const { url, data } = await tryFirstOk(urls);
  if (!url) return;

  const items = extractItems(data);
  safeWrite(cacheKey, { ts: Date.now(), items, meta: { sourceUrl: url } });
}

/* -------------------- exported prefetchers -------------------- */
export async function prefetchLoadsSummaries({ force = false } = {}) {
  const cid = resolveCompanyId();
  const baseKey = cid ? `company:${cid}` : "company:unknown";
  return prefetchList({
    cacheKey: `${baseKey}:loads:summary:v1`,
    urls: ["/loads/summary-with-progress"],
    force,
  });
}

export async function prefetchDriversSummaries({ force = false } = {}) {
  const cid = resolveCompanyId();
  const baseKey = cid ? `company:${cid}` : "company:unknown";
  return prefetchList({
    cacheKey: `${baseKey}:drivers:summary:v1`,
    urls: ["/driveroriginal"],
    force,
  });
}

export async function prefetchClientsSummaries({ force = false } = {}) {
  const cid = resolveCompanyId();
  const baseKey = cid ? `company:${cid}` : "company:unknown";
  const TTL_5MIN = 5 * 60_000;

  // ✅ only use endpoints that actually exist in production
  await prefetchList({
    cacheKey: `${baseKey}:industries:summary:v1`,
    urls: ["/industries/summary"],
    force,
    maxAgeMs: TTL_5MIN,
  });

  await prefetchList({
    cacheKey: `${baseKey}:carriers:summary:v1`,
    urls: ["/carriers/summary"],
    force,
    maxAgeMs: TTL_5MIN,
  });
}

export async function prefetchPlacesList({ force = false } = {}) {
  const cacheKey = "places:list:v1";
  const cache = safeRead(cacheKey);
  const now = Date.now();
  const maxAgeMs = 5 * 60_000;

  const hasFresh =
    !!cache?.items &&
    Array.isArray(cache.items) &&
    (now - (cache.ts || 0) < maxAgeMs);

  if (!force && hasFresh) return;

  try {
    const { data } = await api.get("/places");
    const items = Array.isArray(data) ? data : [];
    safeWrite(cacheKey, { ts: Date.now(), items, meta: {} });
  } catch {}
}

export async function prefetchBillingSummaries({ force = false } = {}) {
  const cid = resolveCompanyId();
  const baseKey = cid ? `company:${cid}` : "company:unknown";

  await prefetchList({
    cacheKey: `${baseKey}:invoices:summary:v1`,
    urls: ["/invoices/summary"],
    force,
  });

  await prefetchList({
    cacheKey: `${baseKey}:purchaseorders:summary:v1`,
    urls: ["/purchaseorders/summary"],
    force,
  });
}


export async function prefetchInvoicesList({ force = false, limit = 20 } = {}) {
  const cacheKey = "invoices_summary_cache_v1";

  const cache = safeRead(cacheKey);
  const now = Date.now();
  const maxAgeMs = 2 * 60_000; // 2 min

  const hasFresh =
    !!cache?.items &&
    Array.isArray(cache.items) &&
    (now - (cache.ts || 0) < maxAgeMs);

  if (!force && hasFresh) return;

  try {
    const { data } = await api.get("/invoices/summary", { params: { limit } });

    const rows = Array.isArray(data?.invoices) ? data.invoices : [];
    const total = typeof data?.total === "number" ? data.total : 0;

    safeWrite(cacheKey, {
      ts: Date.now(),
      items: rows,
      total,
      nextCursor: data?.nextCursor ?? null,
      hasMore: !!data?.hasMore,
    });
  } catch {
    // best effort
  }
}
